import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_BASE } from '../../lib/config';
import {
  WalletIcon,
  BanknotesIcon,
  CurrencyYenIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const API = `${API_BASE}`;

const AdminWalletManagement = ({ onBack }) => {
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [walletStats, setWalletStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNote, setAdminNote] = useState('');

  /* New State for Admin Bank Account */
  const [adminBankInfo, setAdminBankInfo] = useState({
    bank_name: '',
    branch_name: '',
    account_number: '',
    account_holder: '',
    account_type: '普通'
  });
  const [savingBank, setSavingBank] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    checkUserRole();
    fetchData();
    // Always fetch bank info initially (backend guards will protect if unauthorized)
    fetchAdminBankInfo();
  }, [selectedStatus]);

  const checkUserRole = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return;

      const res = await fetch(`${API}/admin/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const user = await res.json();
        const verified = user.role === 'super_admin';
        setIsSuperAdmin(verified);
        // Always fetch bank info if authenticated as admin (auth/me succeeded)
        fetchAdminBankInfo();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchAdminBankInfo = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/admin/bank-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.bank_name) {
          setAdminBankInfo(data);
        }
      }
    } catch (e) {
      console.error('Failed to fetch admin bank info', e);
    }
  };

  const saveAdminBankInfo = async () => {
    if (!adminBankInfo.bank_name || !adminBankInfo.account_number) {
      toast.error('銀行名と口座番号は必須です');
      return;
    }
    try {
      setSavingBank(true);
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/admin/bank-account`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(adminBankInfo)
      });
      if (res.ok) {
        toast.success('口座情報を保存しました');
      } else {
        throw new Error('保存に失敗しました');
      }
    } catch (e) {
      toast.error('保存に失敗しました');
    } finally {
      setSavingBank(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');

      const [requestsRes, statsRes] = await Promise.all([
        fetch(`${API}/admin/withdrawal-requests?status=${selectedStatus}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/admin/wallet-stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (requestsRes.ok) {
        const requestsData = await requestsRes.json();
        setWithdrawalRequests(Array.isArray(requestsData) ? requestsData : []);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setWalletStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  /* Split Payout Info State */
  const [payoutInfo, setPayoutInfo] = useState({
    commission: { available: 0, total_income: 0 },
    subscription: { available: 0, total_income: 0 }
  });

  useEffect(() => {
    // Admin Stats Fetch
    const token = localStorage.getItem('admin_token');
    fetch(`${API}/admin/payout-info`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(res => res.json()).then(data => {
      // Default structure fallback
      const comm = data.commission || { available: data.available_balance || 0, total_income: data.total_commission || 0 };
      const sub = data.subscription || { available: 0, total_income: 0 };
      setPayoutInfo({ commission: comm, subscription: sub });
    }).catch(() => { });
  }, [loading]);

  const handleAdminWithdraw = async (source) => {
    const stream = source === 'subscription' ? payoutInfo.subscription : payoutInfo.commission;
    const label = source === 'subscription' ? '有料プラン売上' : '手数料収益';

    if (stream.available < 1000) {
      toast.error('出金可能額が1,000円未満です');
      return;
    }
    if (!window.confirm(`現在の${label}残高 ${stream.available.toLocaleString()}円 を登録口座へ出金しますか？`)) {
      return;
    }
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/admin/payout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ amount: stream.available, source })
      });
      if (res.ok) {
        toast.success(`${label}の出金処理を開始しました`);
        // Refresh
        fetchData();
        // Optimistic update
        setPayoutInfo(prev => ({
          ...prev,
          [source]: { ...prev[source], available: 0 }
        }));
      } else {
        const err = await res.json();
        toast.error(err.detail || '出金に失敗しました');
      }
    } catch (e) {
      toast.error('エラーが発生しました');
    }
  };

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/withdrawal-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          admin_note: adminNote
        })
      });

      if (response.ok) {
        toast.success('ステータスを更新しました');
        setShowModal(false);
        setSelectedRequest(null);
        setAdminNote('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'ステータス更新に失敗しました');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('ネットワークエラーが発生しました');
    }
  };

  const openModal = (request) => {
    setSelectedRequest(request);
    setAdminNote(request.admin_note || '');
    setShowModal(true);
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return {
          icon: <ClockIcon className="h-4 w-4 text-yellow-500" />,
          label: '申請中',
          color: 'text-yellow-600 bg-yellow-100'
        };
      case 'processing':
        return {
          icon: <ClockIcon className="h-4 w-4 text-blue-500" />,
          label: '処理中',
          color: 'text-blue-600 bg-blue-100'
        };
      case 'completed':
        return {
          icon: <CheckCircleIcon className="h-4 w-4 text-green-500" />,
          label: '完了',
          color: 'text-green-600 bg-green-100'
        };
      case 'failed':
        return {
          icon: <XCircleIcon className="h-4 w-4 text-red-500" />,
          label: '失敗',
          color: 'text-red-600 bg-red-100'
        };
      default:
        return {
          icon: <ClockIcon className="h-4 w-4 text-gray-500" />,
          label: 'その他',
          color: 'text-gray-600 bg-gray-100'
        };
    }
  };

  const safeDate = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('ja-JP');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center mb-6">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            ダッシュボードに戻る
          </button>
        </div>
      )}

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">ウォレット・振込管理</h2>
        <p className="text-gray-600">運営管理者の銀行口座登録と、ユーザーからの振込申請を管理します</p>
      </div>

      <div className="space-y-8">
        {/* 1. Admin Bank Account Form - Always Visible and Prominent */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b pb-2">
            <BanknotesIcon className="h-6 w-6 mr-2 text-blue-600" />
            運営口座情報登録
            <span className="ml-2 text-sm text-gray-500 font-normal">（ここに入力した口座情報は、システム上の重要な設定となります）</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">銀行名</label>
                <input
                  type="text"
                  value={adminBankInfo.bank_name}
                  onChange={e => setAdminBankInfo({ ...adminBankInfo, bank_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="例: 三井住友銀行"
                />
              </div>
              <div className="flex space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">支店名</label>
                  <input
                    type="text"
                    value={adminBankInfo.branch_name}
                    onChange={e => setAdminBankInfo({ ...adminBankInfo, branch_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="例: 渋谷支店"
                  />
                </div>
                <div className="w-1/3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">種別</label>
                  <select
                    value={adminBankInfo.account_type}
                    onChange={e => setAdminBankInfo({ ...adminBankInfo, account_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option>普通</option>
                    <option>当座</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">口座番号</label>
                <input
                  type="text"
                  value={adminBankInfo.account_number}
                  onChange={e => setAdminBankInfo({ ...adminBankInfo, account_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="1234567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">名義人（カナ）</label>
                <input
                  type="text"
                  value={adminBankInfo.account_holder}
                  onChange={e => setAdminBankInfo({ ...adminBankInfo, account_holder: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="カ）バイターズ"
                />
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={saveAdminBankInfo}
              disabled={savingBank}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
            >
              {savingBank ? '保存中...' : '口座情報を保存する'}
            </button>
          </div>
        </div>

        {/* 2. Withdrawal Requests Section */}
        <div>
          {/* フィルター */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">振込申請一覧</h3>
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">ステータス:</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
              >
                <option value="">すべて</option>
                <option value="pending">申請中</option>
                <option value="processing">処理中</option>
                <option value="completed">完了</option>
                <option value="failed">失敗</option>
              </select>
            </div>
          </div>

          {/* 振込申請一覧 Table */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200 hidden">
              {/* Hidden header since we have one above */}
            </div>

            {withdrawalRequests.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">申請者</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">金額</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">振込先</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">申請日</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {withdrawalRequests.map((request) => {
                      const statusInfo = getStatusInfo(request.status);
                      return (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{request.user_name}</div>
                              <div className="text-xs text-gray-500">{request.user_email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-bold text-gray-900">{request.amount.toLocaleString()} <span className="text-xs font-normal">円</span></div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {request.bank_info ? (
                              <div className="text-sm text-gray-900">
                                <div className="font-medium">{request.bank_info.bank_name}</div>
                                <div className="text-xs text-gray-500">
                                  {request.bank_info.branch_name} {request.bank_info.account_number}
                                </div>
                                <div className="text-xs text-gray-500">{request.bank_info.account_holder_name}</div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {safeDate(request.created_at || request.requested_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                              {statusInfo.icon}
                              <span className="ml-1">{statusInfo.label}</span>
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-3">
                              <button onClick={() => openModal(request)} className="text-gray-400 hover:text-blue-600 transition-colors" title="詳細">
                                <EyeIcon className="h-5 w-5" />
                              </button>
                              {request.status === 'pending' && (
                                <button onClick={() => updateRequestStatus(request.id, 'processing')} className="text-gray-400 hover:text-yellow-600 transition-colors" title="処理中にする">
                                  <ClockIcon className="h-5 w-5" />
                                </button>
                              )}
                              {(request.status === 'pending' || request.status === 'processing') && (
                                <>
                                  <button onClick={() => updateRequestStatus(request.id, 'completed')} className="text-gray-400 hover:text-green-600 transition-colors" title="完了にする">
                                    <CheckCircleIcon className="h-5 w-5" />
                                  </button>
                                  <button onClick={() => updateRequestStatus(request.id, 'failed')} className="text-gray-400 hover:text-red-600 transition-colors" title="失敗にする">
                                    <XCircleIcon className="h-5 w-5" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

            )}
          </div>

          {/* 詳細モーダル */}
          {showModal && selectedRequest && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
                <h2 className="text-xl font-semibold mb-4">振込申請詳細</h2>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">申請者</label>
                      <p className="text-sm text-gray-900">{selectedRequest.user_name}</p>
                      <p className="text-sm text-gray-500">{selectedRequest.user_email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">金額</label>
                      <p className="text-lg font-bold text-gray-900">{selectedRequest.amount.toLocaleString()}</p>
                    </div>
                  </div>

                  {selectedRequest.bank_info && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">振込先情報</label>
                      <div className="bg-gray-50 p-3 rounded-lg text-sm">
                        <p><strong>銀行:</strong> {selectedRequest.bank_info.bank_name}</p>
                        <p><strong>支店:</strong> {selectedRequest.bank_info.branch_name}</p>
                        <p><strong>口座番号:</strong> {selectedRequest.bank_info.account_number}</p>
                        <p><strong>名義人:</strong> {selectedRequest.bank_info.account_holder_name}</p>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">申請日時</label>
                    <p className="text-sm text-gray-900">
                      {new Date(selectedRequest.requested_at).toLocaleString('ja-JP')}
                    </p>
                  </div>

                  {selectedRequest.processed_at && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">処理日時</label>
                      <p className="text-sm text-gray-900">
                        {new Date(selectedRequest.processed_at).toLocaleString('ja-JP')}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">管理者メモ</label>
                    <textarea
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={3}
                      placeholder="管理者メモを入力してください"
                    />
                  </div>

                  <div className="flex justify-between pt-4">
                  </div>
                  {isSuperAdmin ? (
                    <div className="space-x-2">
                      {selectedRequest.status === 'pending' && (
                        <button
                          onClick={() => updateRequestStatus(selectedRequest.id, 'processing')}
                          className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors"
                        >
                          処理中にする
                        </button>
                      )}
                      {(selectedRequest.status === 'pending' || selectedRequest.status === 'processing') && (
                        <>
                          <button
                            onClick={() => updateRequestStatus(selectedRequest.id, 'completed')}
                            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                          >
                            完了
                          </button>
                          <button
                            onClick={() => updateRequestStatus(selectedRequest.id, 'failed')}
                            className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
                          >
                            失敗
                          </button>
                        </>
                      )}
                    </div>
                  ) : <div />}
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setSelectedRequest(null);
                      setAdminNote('');
                    }}
                    className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    閉じる
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminWalletManagement;